import { Navigate, Route, Routes, useLocation } from 'react-router';
import { isCompany, useAuth } from '@/lib/auth';
import { CompanyActivityPage, CompanyHomePage, CompanyProductsPage, CompanySettingsPage, StaffPage } from '@/pages/company';
import { Shell } from '@/components/shell';
import { AccountPage } from '@/pages/account';
import { AuditPage } from '@/pages/audit';
import { ClientPage } from '@/pages/client';
import { ClientNewPage } from '@/pages/client-new';
import { ClientHierarchyPage } from '@/pages/client-hierarchy';
import { ClientTicketsPage } from '@/pages/client-tickets';
import { ClientsPage } from '@/pages/clients';
import { DashboardPage } from '@/pages/dashboard';
import { HealthPage } from '@/pages/health';
import { LoginPage } from '@/pages/login';
import { ProductPage } from '@/pages/product';
import { ProductEditPage } from '@/pages/product-edit';
import { ProductsPage } from '@/pages/products';
import { PatternsPage } from '@/pages/patterns';
import { PackagePage } from '@/pages/package';
import { PackageEditPage } from '@/pages/package-edit';
import { PackagesPage } from '@/pages/packages';
import { ResultPage } from '@/pages/result';
import { ResultsPage } from '@/pages/results';
import { DomainsPage, ReleasesPage } from '@/pages/domains';
import { TemplatePage } from '@/pages/template';
import { TemplateEditPage } from '@/pages/template-edit';
import { TemplatesPage } from '@/pages/templates';

/** signed out → the sign-in page; a password that must be changed → the change-password page, and nothing else until it is */
function Guarded() {
  const session = useAuth((s) => s.session);
  const loc = useLocation();
  if (!session) return <Navigate to="/login" replace />;
  if (session.user.mustChangePassword && loc.pathname !== '/account') return <Navigate to="/account" replace />;
  return <Shell />;
}

export function App() {
  const session = useAuth((s) => s.session);
  // a company user's own console (Phase 9): its screens only — the same pages as the Super Admin's, on the company's own API
  if (session && isCompany(session.user))
    return (
      <Routes>
        <Route path="/login" element={<Navigate to="/" replace />} />
        <Route element={<Guarded />}>
          <Route index element={<CompanyHomePage />} />
          <Route path="hierarchy" element={<ClientHierarchyPage />} />
          <Route path="tickets" element={<ClientTicketsPage />} />
          <Route path="products" element={<CompanyProductsPage />} />
          <Route path="settings" element={<CompanySettingsPage />} />
          <Route path="staff" element={<StaffPage />} />
          <Route path="activity" element={<CompanyActivityPage />} />
          <Route path="account" element={<AccountPage />} />
        </Route>
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    );
  return (
    <Routes>
      <Route path="/login" element={session ? <Navigate to="/" replace /> : <LoginPage />} />
      <Route element={<Guarded />}>
        <Route index element={<DashboardPage />} />
        <Route path="clients" element={<ClientsPage />} />
        <Route path="clients/new" element={<ClientNewPage />} />
        <Route path="clients/:clientId" element={<ClientPage />} />
        <Route path="clients/:clientId/tickets" element={<ClientTicketsPage />} />
        <Route path="clients/:clientId/hierarchy" element={<ClientHierarchyPage />} />
        <Route path="products" element={<ProductsPage />} />
        <Route path="products/:code" element={<ProductPage />} />
        <Route path="products/:code/versions/:version/edit" element={<ProductEditPage />} />
        <Route path="packages" element={<PackagesPage />} />
        <Route path="packages/:code" element={<PackagePage />} />
        <Route path="packages/:code/versions/:version/edit" element={<PackageEditPage />} />
        <Route path="templates" element={<TemplatesPage />} />
        <Route path="templates/:code" element={<TemplatePage />} />
        <Route path="templates/:code/versions/:version/edit" element={<TemplateEditPage />} />
        <Route path="patterns" element={<PatternsPage />} />
        <Route path="results" element={<ResultsPage />} />
        <Route path="results/:product/:game/:date/:time" element={<ResultPage />} />
        <Route path="domains" element={<DomainsPage />} />
        <Route path="releases" element={<ReleasesPage />} />
        <Route path="health" element={<HealthPage />} />
        <Route path="audit" element={<AuditPage />} />
        <Route path="account" element={<AccountPage />} />
      </Route>
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}
